import React from 'react'
import { Box, Typography, Button, Stack, styled } from '@mui/material'

// Styled component for the main container to handle responsive padding and gap
const MainContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '80vh',
  padding: theme.spacing(8, 3), // px-6 -> 3 * 8px = 24px
  gap: theme.spacing(6), // gap-12
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    padding: theme.spacing(8, 10), // md:px-20 -> 10 * 8px = 80px
    gap: theme.spacing(12), // md:gap-24
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(8, 16), // lg:px-32 -> 16 * 8px = 128px
  },
  [theme.breakpoints.up('xl')]: {
    padding: theme.spacing(8, 20), // xl:px-40 -> 20 * 8px = 160px
  },
}))

// Styled component for the phone image container to handle responsive width and shadow
const PhoneImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  flexShrink: 0,
  width: 320, // w-[320px]
  filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.15))',
  [theme.breakpoints.up('sm')]: {
    width: 360, // sm:w-[360px]
  },
  [theme.breakpoints.up('md')]: {
    width: 400, // md:w-[400px]
  },
  [theme.breakpoints.up('lg')]: {
    width: 450, // lg:w-[450px]
  },
  [theme.breakpoints.up('xl')]: {
    width: 500, // xl:w-[500px]
  },
}))

const AppDownloadPage = () => {
  return (
    <Box >
      <MainContainer>
        {/* Left: Phone Image */}
        <PhoneImageContainer>
          <Box
            component="img"
            alt="The Smeeless POS application provides a real-time live kitchen display, enabling efficient order management and seamless integration of all incoming orders."
            src="https://firebasestorage.googleapis.com/v0/b/lightworks-tech.appspot.com/o/ruchi-vedika%2Fpos-app-in-tab.png?alt=media&token=72b35da0-49f9-4df4-9274-3615f0c90bdb"
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: '24px', // rounded-3xl
            }}
          />
        </PhoneImageContainer>

        {/* Right: Illustration and Text */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            maxWidth: '500px', // max-w-xl
          }}
        >
          {/* <Box
            component="img"
            alt="Billbuddy POS App"
            src="https://storage.googleapis.com/a1aa/image/a3bc9ea4-02f9-45b0-a520-4e79f63cc9fd.jpg"
            sx={{
              marginBottom: 3, // mb-6 -> 3 * 8px = 24px
              width: 80, // w-20
              height: 80, // h-20
              borderRadius: '12px', // rounded-xl
              boxShadow: 3, // shadow-md
            }}
          /> */}
          <Typography
            variant="h3" // text-3xl sm:text-4xl
            component="h1"
            color='primary'
            sx={{
              fontSize: { xs: '1.875rem', sm: '2.25rem' }, // text-3xl sm:text-4xl
              fontWeight: 800, // font-extrabold
              color: 'grey.900', // text-gray-900
              lineHeight: 'tight', // leading-tight
              marginBottom: 1.5, // mb-3
            }}
          >
            Download Billbuddy POS App
          </Typography>
          <Typography
            variant="body1" // text-base sm:text-lg
            sx={{
              fontSize: { xs: '1rem', sm: '1.125rem' }, // text-base sm:text-lg
              color: 'grey.600', // text-gray-600
              marginBottom: 4, // mb-8 -> 4 * 8px = 32px
              maxWidth: '400px', // max-w-md
            }}
          >
            The Smeeless POS application provides a real-time live kitchen display, enabling efficient order management and seamless integration of all incoming orders.
          </Typography>
          <Stack direction="row" spacing={2}>
            {' '}
            {/* flex gap-4 */}
            <Button
              href="#"
              aria-label="Download on the Apple App Store"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'black',
                color: 'white',
                borderRadius: '6px', // rounded-md
                paddingX: 2, // px-4
                paddingY: 1, // py-2
                boxShadow: 3, // shadow-md
                '&:hover': {
                  boxShadow: 6, // hover:shadow-lg
                  backgroundColor: 'black', // Maintain black background on hover
                },
                textTransform: 'none', // Prevent uppercase transformation
              }}
            >
              <Box
                component="img"
                alt="Apple logo"
                src="https://storage.googleapis.com/a1aa/image/95175bcc-4832-4373-2dee-d931155239e2.jpg"
                sx={{
                  width: 24, // w-6
                  height: 24, // h-6
                  marginRight: 1, // mr-2
                }}
              />
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="caption" sx={{ lineHeight: 'normal' }}>
                  Download on the
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 'semibold', lineHeight: 'normal' }}
                >
                  App Store
                </Typography>
              </Box>
            </Button>
            <Button
              href="#"
              aria-label="Get it on Google Play"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'black',
                color: 'white',
                borderRadius: '6px', // rounded-md
                paddingX: 2, // px-4
                paddingY: 1, // py-2
                boxShadow: 3, // shadow-md
                '&:hover': {
                  boxShadow: 6, // hover:shadow-lg
                  backgroundColor: 'black', // Maintain black background on hover
                },
                textTransform: 'none', // Prevent uppercase transformation
              }}
            >
              <Box
                component="img"
                alt="Google Play logo"
                src="https://storage.googleapis.com/a1aa/image/515db612-6e90-4da9-4259-4197ed02d05d.jpg"
                sx={{
                  width: 24, // w-6
                  height: 24, // h-6
                  marginRight: 1, // mr-2
                }}
              />
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="caption" sx={{ lineHeight: 'normal' }}>
                  GET IT ON
                </Typography>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 'semibold', lineHeight: 'normal' }}
                >
                  Google Play
                </Typography>
              </Box>
            </Button>
          </Stack>
        </Box>
      </MainContainer>
    </Box>
  )
}

export { AppDownloadPage }
